import { IAdminUser } from '@dadohr/shared/domainTypes/adminUser';
import { ICurrentUser } from '@dadohr/shared/domainTypes/currentUser';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { useEffect, useState } from 'react';
import {
  clearSentryUser,
  setSentryOrg,
  setSentryUser
} from '../../sentry';

let currentUser: ICurrentUser | null = null;
// Orgs whose users store has already been warmed this session (see the
// prefetch in setCurrentUser).
const warmedOrgIds = new Set<string>();
type CurrentUserListener = (nextCurrentUser: ICurrentUser | null) => void;

const listeners: CurrentUserListener[] = [];

export const setCurrentUser = (nextCurrentUser: ICurrentUser | null) => {
  currentUser = nextCurrentUser;

  // Update Sentry context when user/org changes
  if (nextCurrentUser) {
    setSentryUser(
      nextCurrentUser.id,
      nextCurrentUser.email,
      nextCurrentUser.displayName || undefined
    );
    setSentryOrg(nextCurrentUser.org.id, nextCurrentUser.org.name);

    // Warm the org-users collection listener as soon as the org is known.
    // This runs on APP BOOT (every page load — auth persists, so there is no
    // "login" event for most sessions), which is exactly when the in-memory
    // store is cold. Every user-shaped surface (employee list, manual-enroll
    // picker, filter dropdowns) attaches to this same session-singleton
    // listener, so starting it here moves the whole-org download (tens of MB
    // for the largest orgs) into the idle seconds right after boot instead of
    // a spinner inside the first surface that needs it.
    //
    // Fires at most ONCE per orgId per session: setCurrentUser is only called
    // on auth-state transitions (useLogin.update — boot resolve, login/logout,
    // org switch), the `warmedOrgIds` guard below skips repeats explicitly,
    // and even without it the store itself is a session singleton
    // (`store[key] || createListener(key)`) — a second getUsersByOrgId call
    // attaches to the SAME Firestore listener, it never re-downloads.
    // requestIdleCallback keeps the warm-up from competing with first paint.
    // Dynamic import because services/user imports services/currentUser
    // (static would be circular).
    const orgId = nextCurrentUser.org.id;
    if (!warmedOrgIds.has(orgId)) {
      warmedOrgIds.add(orgId);
      const idle: (cb: () => void) => void =
        typeof window !== 'undefined' && 'requestIdleCallback' in window
          ? (cb) => (window as Window).requestIdleCallback(cb, { timeout: 5000 })
          : (cb) => {
              setTimeout(cb, 2000);
            };
      idle(() => {
        import('../user')
          .then((m) => m.getUsersByOrgId(orgId))
          .catch(() => undefined);
      });
    }
  } else {
    clearSentryUser();
  }

  listeners.forEach((listener) => listener(nextCurrentUser));
};

// This should only be called after setCurrentUser was called with a non-null value!
export const getCurrentUser = () => {
  return currentUser!;
};

const listenToCurrentUserChanges = (
  listener: CurrentUserListener
): (() => void) => {
  listeners.push(listener);
  return () => {
    const index = listeners.indexOf(listener);
    if (index !== -1) {
      listeners.splice(index, 1);
    }
  };
};

// TODO - this needs to be done context based as well.
// This would allow us to use components which rely on this
// hook in the admin app, where we could just provide a different current user
export const useCurrentUser = () => {
  const [currentUser, setCurrentUser] = useState<ICurrentUser | null>(
    getCurrentUser()
  );

  useEffect(() => listenToCurrentUserChanges(setCurrentUser), []);
  // Can never be null - as this is just used inside the app,
  // if it ever IS null, then we would immediately move to the Login page anyway
  return currentUser!;
};

export const createCurrentUser = (
  authUser: firebase.User,
  user: IAdminUser,
  org: IOrganization,
  tz: string = 'Europe/Berlin'
): ICurrentUser => {
  return {
    id: authUser.uid,
    ...user,
    email: user.email || authUser.email || '',
    photoURL: user.avatar || authUser.photoURL || '',
    displayName: authUser.displayName,
    authUser,
    org,
    tz
  };
};

export const isDadoEmailAddress = (email: string | null | undefined) => {
  return !!(
    email?.endsWith('dadohr.com') || email?.endsWith('emplexp@gmail.com')
  );
};

export const useIsCurrentUserDadoEmployee = () => {
  const currentUser = useCurrentUser();
  return isDadoEmailAddress(currentUser?.email);
};
